import { useState, useCallback } from 'react';
import { Upload, FileSpreadsheet, AlertCircle, CheckCircle2, X, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const SYSTEM_FIELDS = [
  { key: 'title', label: 'Title (not needed if a Job is mapped)', required: false },
  { key: 'division', label: 'Division', required: false },
  { key: 'job', label: 'Job (work card)', required: false },
  { key: 'dueDate', label: 'Due Date', required: true },
  { key: 'responsible', label: 'Responsible (R)', required: true },
  { key: 'accountable', label: 'Accountable (A)', required: false },
  { key: 'consulted', label: 'Consulted (C)', required: false },
  { key: 'informed', label: 'Informed (I)', required: false },
  { key: 'outcome', label: 'Outcome', required: false },
  { key: 'week', label: 'Week', required: false },
  { key: 'instructions', label: 'Instructions', required: false },
  { key: 'links', label: 'Links', required: false },
  { key: 'priority', label: 'Priority', required: false },
  { key: 'status', label: 'Status', required: false },
  { key: 'assignedBy', label: 'Assigned By', required: false },
  { key: 'startDate', label: 'Start Date', required: false },
  { key: 'meetingDate', label: 'Relevant Meeting Date', required: false },
];

export default function BulkImportModal({ open, onOpenChange, accountId, accountName, onSuccess }) {
  const [step, setStep] = useState(1); // 1: Upload, 2: Map Columns, 3: Preview, 4: Complete
  const [file, setFile] = useState(null);
  const [parseResult, setParseResult] = useState(null);
  const [columnMapping, setColumnMapping] = useState({});
  const [validationResult, setValidationResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const resetState = useCallback(() => {
    setStep(1);
    setFile(null);
    setParseResult(null);
    setColumnMapping({});
    setValidationResult(null);
    setImportResult(null);
    setIsLoading(false);
  }, []);

  const handleClose = () => {
    resetState();
    onOpenChange(false);
  };

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv'
    ];
    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const fileExt = '.' + selectedFile.name.split('.').pop().toLowerCase();

    if (!validExtensions.includes(fileExt)) {
      toast.error('Please upload an Excel (.xlsx, .xls) or CSV file');
      return;
    }

    setFile(selectedFile);
    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post(`${API}/api/tasks/bulk-import/parse`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setParseResult(response.data);
      setColumnMapping(response.data.autoMapping || {});
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to parse file');
      setFile(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMappingChange = (systemField, sheetColumn) => {
    setColumnMapping(prev => ({
      ...prev,
      [systemField]: sheetColumn === '_none_' ? null : sheetColumn
    }));
  };

  const validateMapping = async () => {
    // Check required fields
    const missingRequired = SYSTEM_FIELDS
      .filter(f => f.required && !columnMapping[f.key])
      .map(f => f.label);
      if (!columnMapping.title && !columnMapping.job) missingRequired.push('Title or Job (work card)');
      if (columnMapping.job && !columnMapping.division) missingRequired.push('Division (needed when Job is mapped)');

    if (missingRequired.length > 0) {
      toast.error(`Missing required mappings: ${missingRequired.join(', ')}`);
      return;
    }

    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('accountId', accountId);
      formData.append('columnMapping', JSON.stringify(columnMapping));

      const response = await axios.post(`${API}/api/tasks/bulk-import/validate`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setValidationResult(response.data);
      setStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Validation failed');
    } finally {
      setIsLoading(false);
    }
  };

  const executeImport = async () => {
    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('accountId', accountId);
      formData.append('columnMapping', JSON.stringify(columnMapping));

      const response = await axios.post(`${API}/api/tasks/bulk-import/execute`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setImportResult(response.data);
      setStep(4);
      toast.success(response.data.message);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-blue-600" />
            Import Tasks to {accountName}
          </DialogTitle>
        </DialogHeader>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 py-3 border-b">
          {['Upload File', 'Map Columns', 'Preview', 'Complete'].map((label, idx) => (
            <div key={label} className="flex items-center">
              <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm ${
                step > idx + 1 ? 'bg-green-100 text-green-700' :
                step === idx + 1 ? 'bg-blue-100 text-blue-700 font-medium' :
                'bg-gray-100 text-gray-500'
              }`}>
                {step > idx + 1 ? <CheckCircle2 className="h-4 w-4" /> : <span>{idx + 1}</span>}
                <span>{label}</span>
              </div>
              {idx < 3 && <ChevronRight className="h-4 w-4 text-gray-400 mx-1" />}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <div className="flex-1 overflow-auto py-4">
          {/* Step 1: Upload */}
          {step === 1 && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center hover:border-blue-400 transition-colors">
                <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-lg font-medium mb-2">Upload your spreadsheet</p>
                <p className="text-sm text-gray-500 mb-4">Supports Excel (.xlsx, .xls) and CSV files</p>
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="file-upload-input"
                  data-testid="file-upload-input"
                />
                <Button 
                  disabled={isLoading} 
                  data-testid="select-file-btn"
                  onClick={() => document.getElementById('file-upload-input').click()}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Parsing...
                    </>
                  ) : (
                    'Select File'
                  )}
                </Button>
              </div>
              
              <div className="mt-8 text-sm text-gray-600 max-w-md">
                <p className="font-medium mb-2">Required columns:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Task Title</li>
                  <li>Due Date</li>
                  <li>Responsible (person name)</li>
                </ul>
                <p className="mt-3 text-gray-500">
                  Optional: Accountable, Consulted, Informed, Category, Description, etc.
                </p>
              </div>
            </div>
          )}

          {/* Step 2: Map Columns */}
          {step === 2 && parseResult && (
            <div className="space-y-4">
              <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-800">
                <strong>{parseResult.rowCount}</strong> rows found in your file. 
                Map your spreadsheet columns to task fields below.
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {SYSTEM_FIELDS.map(field => (
                  <div key={field.key} className="flex items-center gap-3">
                    <div className="w-40 text-sm">
                      {field.label}
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                    </div>
                    <Select
                      value={columnMapping[field.key] || '_none_'}
                      onValueChange={(v) => handleMappingChange(field.key, v)}
                    >
                      <SelectTrigger className="flex-1" data-testid={`mapping-${field.key}`}>
                        <SelectValue placeholder="Select column..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none_">-- Not mapped --</SelectItem>
                        {parseResult.columns.map(col => (
                          <SelectItem key={col} value={col}>{col}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>

              {/* Preview of first few rows */}
              {parseResult.previewRows?.length > 0 && (
                <div className="mt-6">
                  <p className="text-sm font-medium mb-2">Preview (first 5 rows):</p>
                  <div className="overflow-x-auto border rounded-lg">
                    <table className="min-w-full text-xs">
                      <thead className="bg-gray-50">
                        <tr>
                          {parseResult.columns.slice(0, 6).map(col => (
                            <th key={col} className="px-3 py-2 text-left font-medium">{col}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {parseResult.previewRows.slice(0, 5).map((row, idx) => (
                          <tr key={idx} className="border-t">
                            {parseResult.columns.slice(0, 6).map(col => (
                              <td key={col} className="px-3 py-2 truncate max-w-32">
                                {String(row[col] || '')}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Preview & Validate */}
          {step === 3 && validationResult && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold">{validationResult.totalRows}</div>
                  <div className="text-sm text-gray-600">Total Rows</div>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-green-700">{validationResult.validCount}</div>
                  <div className="text-sm text-green-600">To create{validationResult.duplicateCount > 0 ? ` (${validationResult.duplicateCount} already exist)` : ''}</div>
                </div>
                <div className="bg-red-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-red-700">{validationResult.invalidCount}</div>
                  <div className="text-sm text-red-600">Errors (skipped)</div>
                </div>
              </div>

              {/* Warnings */}
              {validationResult.warnings?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-amber-800 font-medium mb-2">
                    <AlertCircle className="h-4 w-4" />
                    Warnings ({validationResult.warnings.length} rows)
                  </div>
                  <div className="text-sm text-amber-700 max-h-24 overflow-auto">
                    {validationResult.warnings.slice(0, 5).map((w, idx) => (
                      <div key={idx}>Row {w.row}: {w.warnings.join(', ')}</div>
                    ))}
                    {validationResult.warnings.length > 5 && (
                      <div className="text-amber-600">...and {validationResult.warnings.length - 5} more</div>
                    )}
                  </div>
                </div>
              )}

              {/* Errors */}
              {validationResult.errors?.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-red-800 font-medium mb-2">
                    <X className="h-4 w-4" />
                    Errors ({validationResult.errors.length} rows will be skipped)
                  </div>
                  <div className="text-sm text-red-700 max-h-24 overflow-auto">
                    {validationResult.errors.slice(0, 5).map((e, idx) => (
                      <div key={idx}>Row {e.row}: {e.errors.join(', ')}</div>
                    ))}
                    {validationResult.errors.length > 5 && (
                      <div className="text-red-600">...and {validationResult.errors.length - 5} more</div>
                    )}
                  </div>
                </div>
              )}

              {/* Task Preview Table */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 font-medium text-sm border-b">
                  Tasks to Import (showing first 20)
                </div>
                <div className="max-h-64 overflow-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left">Status</th>
                        <th className="px-3 py-2 text-left">Title</th>
                        <th className="px-3 py-2 text-left">Division</th>
                        <th className="px-3 py-2 text-left">Due Date</th>
                        <th className="px-3 py-2 text-left">Priority</th>
                        <th className="px-3 py-2 text-left">Responsible</th>
                        <th className="px-3 py-2 text-left">Accountable</th>
                      </tr>
                    </thead>
                    <tbody>
                      {validationResult.tasks?.slice(0, 20).map((task, idx) => (
                        <tr key={idx} className={`border-t ${task._isValid ? '' : task._action === 'duplicate' ? 'bg-amber-50' : 'bg-red-50'}`}>
                          <td className="px-3 py-2">
                            {task._action === 'duplicate' ? (
                              <span className="text-xs font-medium text-amber-700">Exists</span>
                            ) : task._isValid ? (
                              <CheckCircle2 className="h-4 w-4 text-green-600" />
                            ) : (
                              <X className="h-4 w-4 text-red-600" />
                            )}
                          </td>
                          <td className="px-3 py-2 font-medium">
                            {task.title || '-'}
                            {task.job && task.job !== task.title && <div className="text-xs font-normal text-gray-500">{task.job}</div>}
                            {task._errors?.length > 0 && <div className="text-xs font-normal text-red-600">{task._errors.join('; ')}</div>}
                          </td>
                          <td className="px-3 py-2">{task._division || task.deliverable || '-'}</td>
                          <td className="px-3 py-2">{task.dueDate || '-'}</td>
                          <td className="px-3 py-2">
                            <Badge variant="outline" className={
                              task.priority === 'high' ? 'border-red-300 text-red-700' :
                              task.priority === 'med' ? 'border-amber-300 text-amber-700' :
                              'border-green-300 text-green-700'
                            }>
                              {task.priority}
                            </Badge>
                          </td>
                          <td className="px-3 py-2">{task._responsibleName || '-'}</td>
                          <td className="px-3 py-2">{task._accountableName || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Complete */}
          {step === 4 && importResult && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Import Complete!</h3>
              <p className="text-gray-600 mb-6">
                Successfully imported <strong>{importResult.importedCount}</strong> tasks
                {importResult.skippedCount > 0 && (
                  <span className="text-amber-600"> ({importResult.skippedCount} skipped: errors or already there)</span>
                )}
              </p>
              <Button onClick={handleClose} data-testid="close-import-btn">
                Close
              </Button>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        {step !== 4 && (
          <DialogFooter className="border-t pt-4">
            <div className="flex justify-between w-full">
              <Button variant="outline" onClick={step === 1 ? handleClose : () => setStep(step - 1)}>
                {step === 1 ? 'Cancel' : 'Back'}
              </Button>
              
              {step === 2 && (
                <Button onClick={validateMapping} disabled={isLoading} data-testid="validate-mapping-btn">
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Validating...
                    </>
                  ) : (
                    'Validate & Preview'
                  )}
                </Button>
              )}
              
              {step === 3 && (
                <Button 
                  onClick={executeImport} 
                  disabled={isLoading || validationResult?.validCount === 0}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="execute-import-btn"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    `Import ${validationResult?.validCount || 0} Tasks`
                  )}
                </Button>
              )}
            </div>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
